import { Card, DataList, EmptyView, ErrorView, ListControls, LoadingView, StatusBadge, DashStyles as Styles } from '../../components/dashboard/DashboardUI';
import { FeedbackMessage } from '../../components/dashboard/FeedbackMessage';
import { useNicheLabels } from '../../components/dashboard/useNicheLabels';
import { usePaginatedList } from '../../components/dashboard/usePaginatedList';
import { Localized } from '../../internal/Localized';
import { useDashboard } from '../../providers/DashboardProvider';
import { Button } from '@app/engine';
import { FormatLocalized, FormatTimestamp, Logger, WrapLTR } from '@shared/common';
import { LeadClient, Telemetry } from '@shared/frontend';
import { useState } from 'react';
import type { IFeedbackDTO } from '../../components/dashboard/FeedbackMessage';
import type { ILeadDTO } from '@shared/common';

/** File-local constants. */
const localConstants = { logContext: 'Leads' } as const;

/**
 * Leads screen: a server-paginated, searchable list of captured caller leads as bubbles. Open leads carry a Resolve action that marks them contacted, clearing them from the Overview open-leads
 * count.
 *
 * @returns The leads screen element.
 */
export function Leads() {
  const { tenantID } = useDashboard();
  const list = usePaginatedList(
    (signal, offset, limit, pattern) => LeadClient.ListLeads(signal, tenantID!, offset, limit, pattern),
    (signal, pattern) => LeadClient.CountLeads(signal, tenantID!, pattern),
    [tenantID]
  );
  const labels = useNicheLabels();
  const [busyID, setBusyID] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<IFeedbackDTO | null>(null);

  const resolve = async (lead: ILeadDTO) => {
    setBusyID(lead.id);
    setFeedback(null);
    try {
      const controller = new AbortController();
      await LeadClient.ResolveLead(controller.signal, tenantID!, lead.id);
      Telemetry.LeadResolved();
      setFeedback({ text: Localized.LeadsResolved, type: 'success' });
      list.reload();
    } catch (err: unknown) {
      Logger.Error(localConstants.logContext, Logger.GetErrorMessage(err));
      setFeedback({ text: Localized.DashError, type: 'error' });
    } finally {
      setBusyID(null);
    }
  };

  return (
    <>
      <ListControls
        searchValue={list.search}
        onSearchChange={list.setSearch}
        searchPlaceholder={Localized.LeadsSearchPlaceholder}
        offset={list.offset}
        limit={list.limit}
        total={list.total}
        pageCount={list.data.length}
        onOffsetChange={list.setOffset}
        onLimitChange={list.setLimit}
        loading={list.loading}
      />
      <Card title={FormatLocalized(Localized.PanelListSuffix, labels.leads)} titleHint={Localized.LeadsHint} feedback={<FeedbackMessage feedback={feedback} />}>
        {list.loading ? (
          <LoadingView />
        ) : list.error ? (
          <ErrorView onRetry={list.reload} />
        ) : list.data.length === 0 ? (
          <EmptyView message={Localized.LeadsNone} />
        ) : (
          <div className={Styles.cardStack}>
            {list.data.map((lead) => (
              <div key={lead.id} className={Styles.subPanel}>
                <DataList
                  rows={[
                    { label: Localized.AccountDisplayName, value: lead.name ?? '—' },
                    { label: Localized.SharedContactPhone, value: WrapLTR(lead.phoneE164) },
                    lead.email ? { label: Localized.SharedContactEmail, value: lead.email } : null,
                    lead.reason ? { label: Localized.LeadsReason, value: lead.reason } : null,
                    lead.preferredCallbackISO ? { label: Localized.LeadsCallback, value: FormatTimestamp(lead.preferredCallbackISO) } : null,
                    { label: Localized.SharedWhen, value: FormatTimestamp(lead.createdAt) },
                    {
                      label: Localized.SharedStatus,
                      value: lead.contacted ? (
                        <StatusBadge status="resolved" />
                      ) : (
                        <Button
                          text={Localized.LeadsResolve}
                          disabled={busyID === lead.id}
                          classNameButtonEnabled={`${Styles.btn} ${Styles.btnPill} ${Styles.btnPrimary}`}
                          classNameButtonDisabled={`${Styles.btn} ${Styles.btnPill} ${Styles.btnPrimary}`}
                          onClickHandler={() => void resolve(lead)}
                        />
                      )
                    }
                  ]}
                />
              </div>
            ))}
          </div>
        )}
      </Card>
    </>
  );
}
